import React from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  Badge,
  VStack,
  Icon,
  HStack,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { MdCheckCircle } from "react-icons/md";
import { useUser } from "contexts/UserContext";
import { regions } from "constants/regions";

// Plans Mock Data (Prices as numbers for dynamic formatting)
const plans = [
  {
    name: "Free",
    price: 0,
    requests: "100",
    features: [
      "Basic access to API",
      "Limited rate speed",
      "Community support",
      "Suitable for testing and beginners"
    ],
    recommended: false,
  },
  {
    name: "Pro",
    price: 499,
    billing: "/month",
    requests: "500",
    features: [
      "Faster API response",
      "Priority queue access",
      "Email support",
      "Suitable for small projects"
    ],
    recommended: false,
  },
  {
    name: "Pro Max",
    price: 999,
    billing: "/month",
    requests: "1000",
    features: [
      "High performance APIs",
      "Lower latency",
      "Priority support",
      "Best for production apps"
    ],
    recommended: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    billing: " pricing",
    requests: "Custom",
    features: [
      "Unlimited scalability",
      "Dedicated infrastructure",
      "SLA guarantee",
      "24/7 support",
      "Custom integrations"
    ],
    recommended: false,
  }
];

export default function Pricing() {
  const textColor = useColorModeValue("#1B2559", "#FFFFFF");
  const textColorSecondary = useColorModeValue("#4A5568", "#A3AED0");
  const bgCard = useColorModeValue("#FFFFFF", "#111C44");
  const borderColor = useColorModeValue("#E2E8F0", "#2A3A6B");
  const boxBg = useColorModeValue("#F4F7FE", "#1B254B");

  const { region: globalRegion } = useUser();
  const currentRegion = regions[globalRegion || "INDIA"];
  const currency = currentRegion?.currency || "₹";

  const handleChoosePlan = (planName) => {
    console.log(`Chosen Plan: ${planName}`);
  };

  if (!currentRegion) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }} w="100%" display="flex" justifyContent="center">
        <Spinner size="xl" color="brand.500" />
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex direction="column" align="center" mb="40px" textAlign="center">
        <Text
          color={textColor}
          fontSize="3xl"
          fontWeight="bold"
          mb="10px"
        >
          Pricing Plans
        </Text>
        <Text color={textColorSecondary} fontSize="lg">
          Choose the best plan for your API usage (Region: {globalRegion})
        </Text>
      </Flex>

      <Grid
        templateColumns={{
          base: "1fr",
          md: "repeat(2, 1fr)",
          xl: "repeat(4, 1fr)",
        }}
        gap="20px"
        maxW="1200px"
        mx="auto"
      >
        {plans.map((plan, index) => {
          const isRecommended = plan.recommended;

          return (
            <Card
              key={index}
              p="30px"
              bg={bgCard}
              position="relative"
              border={isRecommended ? "2px solid" : "1px solid"}
              borderColor={isRecommended ? "brand.500" : borderColor}
              transform={isRecommended ? { xl: "scale(1.05)" } : "none"}
              zIndex={isRecommended ? 1 : 0}
              transition="all 0.2s"
              _hover={{ transform: isRecommended ? { xl: "scale(1.08)" } : { xl: "translateY(-5px)" } }}
              display="flex"
              flexDirection="column"
            >
              {isRecommended && (
                <Badge
                  colorScheme="brand"
                  position="absolute"
                  top="-3"
                  left="50%"
                  transform="translateX(-50%)"
                  px="15px"
                  py="5px"
                  borderRadius="full"
                  fontSize="sm"
                >
                  Most Popular
                </Badge>
              )}

              <Text color={textColor} fontSize="xl" fontWeight="600" mb="15px" textAlign="center">
                {plan.name}
              </Text>

              <Flex justify="center" align="baseline" mb="20px">
                <Text color={textColor} fontSize="4xl" fontWeight="700">
                  {plan.price === "Custom" ? "Custom" : `${currency}${plan.price}`}
                </Text>
                {plan.billing && (
                  <Text color={textColorSecondary} fontSize="md" ms="5px">
                    {plan.billing}
                  </Text>
                )}
              </Flex>

              <Box 
                bg={isRecommended ? "rgba(108, 99, 255, 0.1)" : boxBg} 
                p="15px" 
                borderRadius="10px" 
                mb="30px"
                textAlign="center"
              >
                <Text color={isRecommended ? "brand.400" : textColor} fontWeight="bold" fontSize="lg">
                  {plan.requests}
                </Text>
                <Text color={textColorSecondary} fontSize="sm">
                  Requests / day
                </Text>
              </Box>

              <VStack align="start" spacing="15px" mb="40px" flex="1">
                {plan.features.map((feature, i) => (
                  <HStack key={i} align="start">
                    <Icon as={MdCheckCircle} color="brand.400" mt="3px" />
                    <Text color={textColor} fontSize="md" lineHeight="1.5">
                      {feature}
                    </Text>
                  </HStack>
                ))}
              </VStack>

              <Button
                mt="auto"
                colorScheme={isRecommended ? "brand" : "whiteAlpha"}
                variant={isRecommended ? "solid" : "outline"}
                w="100%"
                py="20px"
                onClick={() => handleChoosePlan(plan.name)}
                _light={{
                  color: isRecommended ? "white" : "brand.500",
                  borderColor: isRecommended ? "brand.500" : "brand.501", // force border visibility if needed
                  _hover: { bg: isRecommended ? "brand.600" : "secondaryGray.100" }
                }}
              >
                Choose Plan
              </Button>
            </Card>
          );
        })}
      </Grid>
    </Box>
  );
}

